import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel } from "./ui";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Clock, Wrench, AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const downloadFile = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const TEAL = "#00857C";
const NAVY = "#0B2545";
const AGE_COLORS = { "0-30": "#00857C", "31-60": "#0369a1", "61-90": "#b45309", "90+": "#dc2626" };

const Kpi = ({ icon: Icon, label, value, suffix, hint, danger }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5" style={{ borderTop: `3px solid ${danger ? "#dc2626" : TEAL}` }}>
    <div className="flex items-center justify-between">
      <div className="overline text-slate-500">{label}</div>
      <Icon className={`h-4 w-4 ${danger ? "text-red-600" : "text-[#00857C]"}`} />
    </div>
    <div className="font-head text-3xl font-bold text-[#0B2545] mt-2">{value}<span className="text-lg text-slate-400">{suffix}</span></div>
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </div>
);

export default function Reports() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/kpis").then((r) => setD(r.data)); }, []);
  if (!d) return null;

  const canExport = ["finance", "operations_controller", "system_admin"].includes(user.role);
  const canAgeing = ["finance", "operations_controller", "warehouse_manager", "system_admin"].includes(user.role);

  const runAgeingCheck = async () => {
    try {
      const { data } = await api.post("/reports/ageing-check");
      if (data.count === 0) toast.info("No units over 90 days — nothing to alert.");
      else toast.success(`${data.count} ageing unit(s) flagged — Finance notified.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Reporting & KPIs" subtitle="Ageing, install velocity and shrinkage across the consignment network.">
        {canAgeing && (
          <Button data-testid="run-ageing-check" onClick={runAgeingCheck} variant="outline" className="rounded-sm border-slate-300 font-head">
            <AlertTriangle className="h-4 w-4 mr-2" /> Run ageing check
          </Button>
        )}
        {canExport && (
          <>
            <Button data-testid="export-ageing-csv" onClick={() => downloadFile("/reports/ageing.csv", "netstar_ageing.csv")} variant="outline" className="rounded-sm border-slate-300 font-head">
              <Download className="h-4 w-4 mr-2" /> Ageing CSV
            </Button>
            <Button data-testid="export-finance-pdf-reports" onClick={() => downloadFile("/reports/finance.pdf", "netstar_finance_report.pdf")} className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head">
              <Download className="h-4 w-4 mr-2" /> Finance PDF
            </Button>
          </>
        )}
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Clock} label="Ageing > 90 days" value={d.ageing_over90_pct} suffix="%" hint="of live consignment stock" danger={d.ageing_over90_pct >= 5} />
        <Kpi icon={Wrench} label="Total Installs" value={d.installs_total} hint="all job types captured" />
        <Kpi icon={CheckCircle2} label="Activated (IP2)" value={d.active} hint="units billed / active" />
        <Kpi icon={AlertTriangle} label="Shrinkage" value={d.shrinkage_pct} suffix="%" hint={`${d.written_off} written off of ${d.total_issued}`} danger={d.shrinkage_pct >= 0.5} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Consignment Stock Ageing (live stock)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.ageing} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {d.ageing.map((e) => <Cell key={e.bucket} fill={AGE_COLORS[e.bucket]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-xs text-slate-400 mt-1">Days since issued to bin (IP1). Target: &lt; 5% over 90 days.</div>
        </Panel>
        <Panel title="Install Velocity (last 14 days)">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.velocity} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip />
              <Line type="monotone" dataKey="installs" stroke={TEAL} strokeWidth={2} dot={{ r: 2, fill: NAVY }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="text-xs text-slate-400 mt-1">Fitments / job-orders captured per day.</div>
        </Panel>
      </div>
    </div>
  );
}
